import { KGDataFrameProto } from "build-ts/dataframe";
import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType } from "build-ts/common";
import React, { useMemo, useState, useEffect } from "react";
import LanggraphMessageDisplayComponent from "components/agentChat/LanggraphMessageDisplayComponent";
import {
  Box,
  HStack,
  Tag,
  TagLabel,
  Icon,
  IconButton,
  Tooltip,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Checkbox,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Collapse,
  Portal
} from "@chakra-ui/react";
import { FaTimes, FaExclamationTriangle, FaEdit, FaChevronDown, FaChevronUp, FaRegFileExcel } from "react-icons/fa";
import { FaTableCellsLarge } from "react-icons/fa6";
import { IoCopyOutline } from "react-icons/io5";
import { FilterProto } from "build-ts/metadata";
import { getOperatorSymbol } from "utils/analysisUtils";
import { getDateFilterName } from "utils/nameUtils";
import DataFrameTable from "./DataFrameTable";
import { extractRawDataFromDataFrame, copyRawTableToClipboard, downloadRawTableAsExcel } from "components/analyse/data_table/dataTableUtils";
import { populateArtifact } from "services/DataService";
import { FiDatabase } from "react-icons/fi";
import { TbMathFunction } from "react-icons/tb";
import { SiGooglebigquery } from "react-icons/si";
import { DownloadIcon } from "@chakra-ui/icons";
type TabId = "data" | "filters" | "plan";

interface DataFrameDisplayComponentProps {
  name?: string;
  data: KGDataFrameProto;
  isSelected?: boolean;
  onClose?: () => void;
  /** When provided, a toggle button appears in the header to switch between the table and the analysis plan. */
  langgraphMessage?: string[];
  /** When this value changes (and is truthy), the component collapses. */
  collapseSignal?: number;
}

const DataFrameDisplayComponent: React.FC<DataFrameDisplayComponentProps> = ({ name, data, isSelected, onClose, langgraphMessage, collapseSignal }) => {
  // Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [displayedData, setDisplayedData] = useState<KGDataFrameProto>(data);
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("data");
  // number of rows in the dataframe
  const [numberOfRows, setNumberOfRows] = useState(0);

  // Track which expression filters are checked
  const [checkedExpressionFilters, setCheckedExpressionFilters] = useState<{ [key: string]: boolean }>(() => {
    const initial: { [key: string]: boolean } = {};
    data.expression_filters.forEach((filter) => {
      initial[filter] = true; // All checked by default
    });
    return initial;
  });

  // Collapse the artifact when the collapse signal changes
  useEffect(() => {
    if (collapseSignal) {
      setIsExpanded(false);
    }
  }, [collapseSignal]);

  // Function to handle Get Data
  const handleGetData = async () => {
    setIsEditModalOpen(false);
    const selectedFilters = Object.entries(checkedExpressionFilters)
      .filter(([_, isChecked]) => isChecked)
      .map(([filter, _]) => filter);

    const modifiedData: KGDataFrameProto = {
      ...data,
      expression_filters: selectedFilters,
    };

    const artifactRequest = ArtifactProto.create({
      artifact_type: ArtifactType.KG_DATAFRAME,
      kg_dataframe: modifiedData
    });
    const response = await populateArtifact(artifactRequest);
    const dataframeData = response?.kg_dataframe;
    if (dataframeData) {
      setDisplayedData(dataframeData);
    }
  };

  // Process aggregate columns (is_aggregate_expression is true)
  const aggregateColumns = useMemo(() => {
    return displayedData.ordered_columns
      .filter((column) => column.is_aggregate_expression)
      .map((column) => {
        const name = column.output_column_name || "";
        const eval_expression = column.eval_expression || "";

        let value: any = null;
        if (column.data_value_json_array) {
          try {
            const sanitizedJson = column.data_value_json_array
              .replace(/\bNaN\b/g, "null")
              .replace(/\bInfinity\b/g, "null")
              .replace(/\b-Infinity\b/g, "null");
            const parsedValues = JSON.parse(sanitizedJson);
            value = Array.isArray(parsedValues) ? parsedValues[0] : parsedValues;
          } catch (error) {
            console.error(`Failed to parse aggregate data for column ${name}:`, error);
          }
        }

        return {
          name,
          eval_expression,
          value: value !== null && value !== undefined ? String(value) : "N/A",
        };
      });
  }, [displayedData]);

  // Function to render filters with grouping
  const renderFilters = () => {
    // Get filters from kg_observation
    const filters: FilterProto[] = data.kg_observation?.filters || [];
    const expressionFilters = data.expression_filters || [];

    if (filters.length === 0 && expressionFilters.length === 0) return null;

    // Group filters by column name
    const groupedFilters = filters.reduce((acc, filter) => {
      const key = filter?.kg_column_header?.name || "";

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(filter);
      return acc;
    }, {} as Record<string, FilterProto[]>);

    return (
      <Box overflowX="auto" overflowY="visible">
        <Box display="flex" flexWrap="wrap" gap={1} py={1} alignContent="flex-start" pl={1}>
          {/* Regular filters from kg_observation */}
          {Object.entries(groupedFilters).map(([key, filtersGroup], index) => {
            // Render all filters consistently
            return filtersGroup.map((filter, i) => (
              <Tag
                key={`filter-${index}-${i}`}
                size="sm"
                borderRadius="md"
                variant="solid"
                bg="white"
                color="gray.800"
                borderWidth="1px"
                borderColor="gray.300"
                p={1}
              >
                <TagLabel
                  fontSize="xs"
                  textAlign="left"
                  whiteSpace="nowrap"
                  fontFamily="mono"
                >
                  <Box as="span" fontWeight="700" color="gray.900">
                    {filter?.kg_column_header?.name}
                  </Box>
                  <Box as="span" fontWeight="500" color="gray.800">
                    {" "}
                    {getOperatorSymbol(filter?.operator!)}
                    {" "}
                    {filter?.values?.length > 0
                      ? filter.values.map((value: any) => value.toString().trim()).join(", ")
                      : filter?.date_filter?.absolute_date_filter
                        ? getDateFilterName(filter?.date_filter?.absolute_date_filter!)
                        : filter?.date_filter
                          ? getDateFilterName(filter?.date_filter!)
                          : ""}
                  </Box>
                </TagLabel>
              </Tag>
            ));
          })}
        </Box>
      </Box>
    );
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  // Check if there are any filters to display
  const hasFilters = useMemo(() => {
    const filters = data.kg_observation?.filters || [];
    const expressionFilters = data.expression_filters || [];
    return filters.length > 0 || expressionFilters.length > 0;
  }, [data]);

  const tabs: { id: TabId; label: string; icon: React.ElementType; iconSize?: number }[] = [
    { id: "data", label: "Data", icon: FiDatabase },
    ...(langgraphMessage ? [{ id: "plan" as TabId, label: "Query", icon: SiGooglebigquery, iconSize: 3.5 }] : []),
    ...(langgraphMessage ? [{ id: "filters" as TabId, label: "Calculations", icon: TbMathFunction, iconSize: 4 }] : []),
  ];

  return (
    <Box
      position="relative"
      overflow="hidden"
      p={0}
      width="100%"
      height="auto"
      transition="all 0.2s ease-in-out"
    >
      {/* ── Card Header ─────────────────────────────────── */}
      <HStack
        px={2}
        py={1}
        bg="gray.50"
        borderBottom="1px solid"
        borderColor="gray.900"
        cursor="pointer"
        onClick={() => setIsExpanded((prev) => !prev)}
        justifyContent="space-between"
        transition="background 0.15s"
      >
        <HStack spacing={2} flex={1} minWidth={0}>
          <Icon as={FaTableCellsLarge} boxSize={4} color="purple.700" flexShrink={0} />
          {name && (
            <Text fontSize="sm" fontWeight="600" color="gray.800" m={0} isTruncated>
              {name}
            </Text>
          )}
        </HStack>

        {/* Action Buttons */}
        <HStack spacing={0} flexShrink={0} onClick={(e) => e.stopPropagation()}>
          {/* Number of rows present */}
          {numberOfRows > 0 && (
            <Text fontSize="xs" color="gray.700" mx={2} mb={0}>
              {numberOfRows} {numberOfRows === 1 ? "row" : "rows"}
            </Text>
          )}

          {/* Settings Menu: Copy, Download, Edit Filters */}
          <Menu placement="bottom-end" gutter={2} closeOnSelect={true}>
            <MenuButton
              as={IconButton}
              size="xs"
              color="gray.600"
              bg="transparent"
              _hover={{ bg: "gray.50", color: "gray.800" }}
              aria-label="Settings"
              icon={
                <HStack spacing={0}>
                  <Icon as={DownloadIcon} boxSize={5} />
                </HStack>
              }
              onClick={(e) => e.stopPropagation()}
              title="Settings"
            />
            <Portal>
              <MenuList
                minW="120px"
                zIndex={1000}
                shadow="lg"
                border="1px solid"
                borderColor="gray.200"
                p={1}
                position="relative"
              >
                {data.expression_filters && data.expression_filters.length > 0 && (
                  <MenuItem
                    icon={<Icon as={FaEdit} boxSize={4} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditModalOpen(true);
                    }}
                    fontSize="sm"
                  >
                    Edit Filters
                  </MenuItem>
                )}
                <MenuItem
                  icon={<Icon as={IoCopyOutline} boxSize={4} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    const rawData = extractRawDataFromDataFrame(displayedData);
                    copyRawTableToClipboard(rawData);
                  }}
                  fontSize="sm"
                >
                  Copy to clipboard
                </MenuItem>
                <MenuItem
                  icon={<Icon as={FaRegFileExcel} boxSize={4} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    const rawData = extractRawDataFromDataFrame(displayedData);
                    downloadRawTableAsExcel(rawData, "dataframe_export.xlsx");
                  }}
                  fontSize="sm"
                >
                  Download Excel file
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>

          {/* Error Warning Icon */}
          {displayedData.error_messages && displayedData.error_messages.length > 0 && (
            <Tooltip
              label={
                <Box as="ul" pl={4} maxW="300px" fontSize="xs">
                  {displayedData.error_messages.map((error, index) => (
                    <Box as="li" key={index}>{error}</Box>
                  ))}
                </Box>
              }
              placement="bottom"
              hasArrow
              bg="red.600"
            >
              <IconButton
                icon={<Icon as={FaExclamationTriangle} boxSize={4} />}
                aria-label="Show errors"
                size="xs"
                color="red.500"
                bg="transparent"
                _hover={{ bg: "red.50", color: "red.600" }}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          )}

          {/* Close */}
          {onClose && (
            <IconButton
              icon={<Icon as={FaTimes} boxSize={4} />}
              onClick={handleClose}
              aria-label="Close"
              size="xs"
              color="red.500"
              colorScheme="gray"
              variant="ghost"
            />
          )}
        </HStack>

        <Icon
          as={isExpanded ? FaChevronUp : FaChevronDown}
          boxSize={3}
          color="gray.600"
          mr={1}
        />
      </HStack>

      {/* ── Collapsible Body ─────────────────────────────── */}
      <Collapse in={isExpanded} animateOpacity>
        <Box>
          {/* ── Tab Bar ── */}
          <HStack
            spacing={0}
            borderBottom="1px solid"
            borderColor="gray.200"
            bg="white"
            px={2}
            width="100%"
          >
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <HStack
                  key={tab.id}
                  flex={1}
                  justifyContent="center"
                  spacing={1.5}
                  px={3}
                  py={1}
                  cursor="pointer"
                  borderBottom="2px solid"
                  borderColor={isActive ? "purple.500" : "transparent"}
                  color={isActive ? "purple.600" : "gray.500"}
                  _hover={{ color: isActive ? "purple.600" : "gray.700" }}
                  transition="all 0.15s"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveTab(tab.id);
                  }}
                  mb="-1px"
                >
                  <Icon as={tab.icon} boxSize={tab.iconSize ?? 3.5} />
                  <Text fontSize="xs" fontWeight={isActive ? "700" : "500"} mb={0}>
                    {tab.label}
                  </Text>
                </HStack>
              );
            })}
          </HStack>

          {/* ── Tab Content ── */}
          <Box
            overflowY="auto"
            maxH="70vh"
            onWheel={(e) => {
              e.stopPropagation();
              e.currentTarget.scrollTop += e.deltaY;
            }}
            sx={{
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
            }}
          >
            {/* Data Tab */}
            {activeTab === "data" && (
              <>
                <DataFrameTable
                  data={displayedData}
                  setNumberOfRows={setNumberOfRows}
                />
                {aggregateColumns.length > 0 && (
                  <Box p={2} bg="purple.50" borderTopWidth="1px" borderColor="gray.200">
                    <HStack spacing={2} flexWrap="wrap">
                      {aggregateColumns.map((aggCol, index) => (
                        <Box key={index}>
                          <Text fontSize="sm" fontWeight="medium">
                            <Tooltip
                              label={aggCol.eval_expression || "No expression available"}
                              placement="top"
                              hasArrow
                              bg="gray.700"
                            >
                              <Text as="span" color="gray.700" cursor="pointer">
                                {aggCol.name}
                              </Text>
                            </Tooltip>
                            <Text as="span" color="gray.700" mx={1}>=</Text>
                            <Text as="span" color="purple.600" fontWeight="semibold">
                              {aggCol.value}
                            </Text>
                          </Text>
                        </Box>
                      ))}
                    </HStack>
                  </Box>
                )}
              </>
            )}

            {/* Query Tab: metric columns, filters, group by, comparison, benchmark */}
            {activeTab === "plan" && langgraphMessage && (
              <Box px={1} py={1} pt={2}>
                <LanggraphMessageDisplayComponent
                  langgraphMessage={langgraphMessage}
                  onClose={() => setActiveTab("data")}
                  isSelected={false}
                  section="query"
                  hideCloseButton
                  presetFiltersNode={hasFilters ? renderFilters() : undefined}
                  useParentScroll
                />
              </Box>
            )}

            {/* Calculations Tab: langgraph calculated columns + expression filters */}
            {activeTab === "filters" && langgraphMessage && (
              <Box px={1} py={1} pt={2}>
                <LanggraphMessageDisplayComponent
                  langgraphMessage={langgraphMessage}
                  onClose={() => setActiveTab("data")}
                  isSelected={false}
                  section="calculations"
                  hideCloseButton
                  useParentScroll
                />
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>

      {/* Edit Filters Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Expression Filters</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              {data.expression_filters.map((filter, index) => (
                <Checkbox
                  key={index}
                  isChecked={checkedExpressionFilters[filter]}
                  onChange={(e) => {
                    setCheckedExpressionFilters(prev => ({
                      ...prev,
                      [filter]: e.target.checked
                    }));
                  }}
                  colorScheme="purple"
                >
                  <Text fontSize="sm" m={0}>{filter}</Text>
                </Checkbox>
              ))}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="purple" onClick={handleGetData}>
              Get Data
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DataFrameDisplayComponent;